<script setup lang="ts">
import { actionTransformer } from '~/components/cms/Action/actionHelpers'
import Picture from '~/components/cms/Media/Picture.vue'
import { AnchorProps } from '~/components/ui/Anchor.vue'
import { Panel } from '~/components/ui/Menu/MenuPanels.vue'
import { BREAKPOINTS } from '~/config/common'

type Props = {
  open: boolean
  id: string
  title: string
  parentTitle: string
  panelLink?: AnchorProps
  panels?: Panel[]
  supportingData?: any
}

const props = defineProps<Props>()
const emits = defineEmits(['menuSubPanelClose', 'menuPanelClose'])

const visiblePanels = computed(() => props.panels?.filter((panel) => personalizationControl(panel.personalizationTags)))

const desktopHiddenIndices = computed(() =>
  // use this approach to handle hiding items on desktop using css to avoid hydration errors
  (visiblePanels.value ?? []).map((panel) => {
    const maxDesktopItems = panel.supportingData?.maxDesktopItems
    if (!maxDesktopItems || !panel.actions?.length) return new Set<number>()
    const lastIndex = panel.actions.length - 1
    const hidden = new Set<number>()
    for (let j = maxDesktopItems; j < lastIndex; j++) {
      hidden.add(j)
    }
    return hidden
  }),
)

const trackMenuBackEvent = () => {
  pushEventToDataLayer('custom.menu.back', {
    group: 'mobile menu',
    label: props.title,
    type: 'navigationItem',
    info: {
      location: 'sidebar',
      element: document.getElementById(props.id)?.tagName,
    },
  })
}

const gaOptions: DataLayerOptional = {
  eventName: 'custom.menu.link',
  type: 'UtilityPanelNavigation',
  runtimeProps: [
    { name: 'group', value: () => (window.innerWidth >= BREAKPOINTS.desktopXL ? 'mega menu' : 'mobile menu') },
  ],
  info: {
    location: 'header',
  },
}

const handleHeaderBack = (e: MouseEvent) => {
  emits('menuSubPanelClose', e, true)
  trackMenuBackEvent()
}
</script>

<template>
  <div
    class="menu-sub-panels"
    data-testid="menu-sub-panels"
    :id="id"
    :class="open ? 'menu-sub-panels--open' : 'menu-sub-panels--close'"
  >
    <div class="menu-sub-panels__header">
      <div class="menu-sub-panels__header-row">
        <button class="menu-sub-panels__header-back" @click="handleHeaderBack">
          <svg-icon name="mhj:arrow-carousel-left" /> {{ $t('header.menu_drawer.buttons.back') }}
        </button>
        <button
          class="menu-sub-panels__header-close"
          @click="$emit('menuPanelClose', $event)"
          :title="$t('screen_reader.buttons.close')"
        >
          <svg-icon name="mhj:cross" />
        </button>
      </div>
      <div class="menu-sub-panels__header-titles">
        <span class="menu-sub-panels__header-parent">{{ parentTitle }}</span>
        <h3 class="menu-sub-panels__header-title">{{ title }}</h3>
      </div>
    </div>

    <ul class="menu-sub-panels__scroll">
      <template v-if="visiblePanels">
        <template v-for="(panel, i) in visiblePanels">
          <li v-if="panel.actions" class="menu-sub-panels__panel">
            <template v-if="i === 0">
              <Anchor
                v-if="panelLink"
                v-bind="panelLink"
                :add-to-site-navigation-schema="true"
                class="menu-sub-panels__panel-link"
              />
            </template>
            <h5 v-if="panel.title" class="menu-sub-panels__heading">{{ panel.title }}</h5>
            <ul
              :class="{
                'menu-sub-panels__list--with-picture': !!panel.actions?.[0]?.fields?.quickLinkPicture,
                'menu-sub-panels__list--wide': panel.supportingData?.width?.value === 'wide',
                'menu-sub-panels__list--swatch': panel.supportingData?.variant?.value === 'swatchLinks',
              }"
            >
              <template v-for="(action, j) in panel.actions">
                <li
                  v-if="action?.fields?.text"
                  class="menu-sub-panels__list-item"
                  :class="{ 'menu-sub-panels__list-item--hidden': desktopHiddenIndices[i]?.has(j) }"
                >
                  <Picture
                    v-if="action.fields?.quickLinkPicture"
                    class="menu-sub-panels__link-picture"
                    v-bind="action.fields.quickLinkPicture"
                  />
                  <Anchor
                    data-testid="menu__link"
                    v-bind="actionTransformer(action)"
                    :ga-options="gaOptions"
                    :log-click="true"
                    :add-to-site-navigation-schema="true"
                  />
                </li>
              </template>
            </ul>
          </li>
          <li v-if="panel.type === 'richTextPanel'" class="menu-sub-panels__panel">
            <template v-if="i === 0">
              <Anchor
                v-if="panelLink"
                v-bind="panelLink"
                :add-to-site-navigation-schema="true"
                class="menu-sub-panels__panel-link"
              />
            </template>
            <RichTextPanel :content="panel?.content" :skipContainer="true" />
          </li>
        </template>
      </template>
    </ul>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.menu-sub-panels {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: var(--surface-light-00);
  display: none;
  animation: slide-out-to-right 0.3s cubic-bezier(0.25, 0, 0.3, 1); /* closing effect */

  &.menu-sub-panels--open {
    display: block;
    z-index: var(--z-header);
    animation: slide-in-from-right 0.5s cubic-bezier(0.25, 0, 0.3, 1); /* opening effect */
    overflow: auto;
    overscroll-behavior: none;
    scrollbar-width: none;
  }

  .menu-sub-panels__header {
    background: var(--surface-light-00);
    border-bottom: 1px solid var(--border-neutral-20);
    position: sticky;
    top: 0;
    padding-block: var(--space-50) var(--space-60);

    .menu-sub-panels__header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-inline: var(--space-50);
    }

    .menu-sub-panels__header-back {
      display: flex;
      align-items: center;
      gap: var(--space-30);
      font-size: var(--text-small);
      cursor: pointer;

      svg {
        height: var(--space-50);
      }
    }

    .menu-sub-panels__header-titles {
      display: flex;
      flex-direction: column;
      gap: var(--space-20);
      align-items: center;
      justify-content: center;
    }
    .menu-sub-panels__header-parent {
      display: block;
      font-size: var(--text-small);
      text-transform: uppercase;
      color: var(--text-secondary);
      letter-spacing: 0.015rem;
    }

    .menu-sub-panels__header-title {
      @extend %heading-2;
      text-transform: uppercase;
      margin: 0;
    }
  }

  .menu-sub-panels__scroll {
    overflow-x: hidden;
    scrollbar-width: none;
    padding-inline: var(--space-50);
  }

  .menu-sub-panels__heading {
    font-size: var(--text-base);
    font-weight: bold;
    margin-bottom: var(--space-60);
    text-transform: uppercase;
    min-width: max-content;
    max-width: 13.5rem;
  }

  .menu-sub-panels__panel {
    width: 100%;
    padding-top: var(--space-60);
    border-bottom: 1px solid var(--border-neutral-20);

    &:last-child {
      border-bottom: none;
    }

    li {
      margin-bottom: var(--space-60);
    }

    a {
      display: flex;
      text-decoration: none;
      font-size: var(--text-large);
      @media (--desktop) {
        font-size: var(--text-small);
      }
      &:hover {
        text-decoration: underline;
      }

      &:has(.icon) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: var(--space-30);
        padding: var(--space-30) 0;
        width: 4.5rem;
        text-align: center;
      }
    }

    a.link-text {
      font-weight: bold;
    }

    .menu-sub-panels__panel-link {
      text-decoration: none;
      font-size: var(--text-base);
      font-weight: bold;
      width: 100%;
      padding-bottom: var(--space-60);
      border-bottom: 1px solid var(--border-neutral-20);
      margin-bottom: var(--space-60);
    }

    .rich-text__block-entry {
      margin: 0;
    }

    .menu-sub-panels__list--swatch {
      a {
        flex-direction: row;
        line-height: var(--text-small);
        justify-content: flex-start;
        padding-block: var(--space-40);
        min-width: fit-content;

        .icon__img {
          width: 1.25rem;
          height: 1.25rem;
        }
      }

      li {
        margin-bottom: 0 !important;
      }
    }
  }

  .menu-sub-panels__list-item--hidden {
    @media (--desktop) {
      display: none;
    }
  }

  .menu-sub-panels__link-picture {
    @media (--desktop) {
      display: none;
    }
    .picture__img {
      aspect-ratio: var(--aspect-ratio-5-4);
      width: 100%;
      object-fit: cover;
    }
  }
}

@media (--mobile-tablet-only) {
  .menu-sub-panels,
  .menu-sub-panels__header {
    max-width: 28rem;
  }
  .menu-sub-panels {
    .menu-sub-panels__heading {
      display: none;
    }
    .menu-sub-panels__list--with-picture {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: var(--space-50);
    }

    .menu-sub-panels__list--wide {
      display: grid;
      grid-template-columns: repeat(3, auto);
      gap: var(--space-60);
      justify-items: center;

      .icon__img {
        min-width: var(--space-100);
        height: var(--space-100);
      }

      li {
        margin-bottom: 0 !important;
      }
    }
  }
}

@media (--desktop) {
  .menu-sub-panels {
    display: contents;
    animation: none;

    &.menu-sub-panels--open {
      display: contents;
      animation: none;
    }

    .menu-sub-panels__header {
      display: none;
    }

    .menu-sub-panels__scroll {
      display: contents;
    }

    .menu-sub-panels__panel {
      min-width: min-content;
      border-bottom: none;
      grid-row: 1 / -1;
      padding-right: var(--space-60);

      &:has(.icon) {
        .menu-sub-panels__heading {
          margin-bottom: var(--space-50);
        }
        margin-bottom: var(--space-30);
      }
    }

    .menu-sub-panels__panel:has(.menu-sub-panels__list--wide) {
      grid-row: auto;
    }

    .menu-sub-panels__list--wide {
      display: grid;
      grid-template-columns: repeat(4, auto);
      column-gap: var(--space-50);
      row-gap: var(--space-30);

      .icon__img {
        min-width: var(--space-70);
        height: var(--space-70);
      }

      li {
        margin-bottom: 0 !important;
      }

      .anchor {
        max-width: 13.5rem;
        &:hover {
          background-color: var(--surface-light-10);
        }
      }
    }
    &:has(.menu-sub-panels__list--swatch) {
      .menu-sub-panels__heading {
        margin-bottom: var(--space-40);
      }
    }
  }
}
</style>
